import { useState, useEffect, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { CheckCircle2, XCircle, Loader2, ExternalLink, RefreshCw, ShieldCheck } from 'lucide-react'
import { useProgress } from '../context/ProgressContext'
import { useAuth } from '../context/AuthContext'
import { sheetVerdict } from '../lib/completionTruth'
import { getSheetsAccessToken, clearSheetsToken, hasSheetsToken } from '../lib/sheetsAuth'
import { runChecks, parseSpreadsheetId, sheetUrlFor } from '../lib/sheetsCheck'
import { scopedKey, clearLegacyKey } from '../lib/localKeys'

const LS_PREFIX = 'sheet-url'

/**
 * Ask the trainee for their spreadsheet, read it as them, and check it.
 *
 * `checks` is an array of { label, range|sheets, assert } — see lib/sheetsCheck.
 * `sourceModuleId` reuses the URL captured by an earlier module so nobody has
 * to paste the same link seven times.
 *
 * Passing marks the module complete and records the score, so a sheet module
 * and a quiz module mean the same thing in the admin console.
 */
export default function SheetSubmission({
  moduleId,
  checks = [],
  sourceModuleId = null,
  heading = 'Check your sheet',
  intro = 'Paste the link to your spreadsheet and we\'ll check it against what this module asked for.',
}) {
  const { saveModuleProgress, completeModule, startModule, getModuleProgress, loading } = useProgress()
  const { user } = useAuth()

  const saved = getModuleProgress(moduleId)
  const inherited = sourceModuleId != null ? getModuleProgress(sourceModuleId) : null
  const lsKey = scopedKey(LS_PREFIX, user?.uid, moduleId)

  const [url, setUrl] = useState('')
  const [status, setStatus] = useState('idle') // idle | checking | done
  const [error, setError] = useState(null)
  // Set when the checks passed but the write recording that failed. Kept apart
  // from `error` because the trainee's sheet is fine and their work is not.
  const [saveFailed, setSaveFailed] = useState(false)
  // A check run on this page, which outranks the stored verdict because it is
  // newer. Nothing else in this component holds a verdict.
  const [sessionResult, setSessionResult] = useState(null)
  const hydrated = useRef(false)

  /**
   * The verdict to show, and whether a retest is due — one value, one reader.
   *
   * This used to be state seeded from the progress document and then patched by
   * a private `getDoc` staleness check, while a prefill effect restored it from
   * the context. Two reads racing to own the same field is how the trainee could
   * see "All checks passed" and "your trainer has asked you to do this again" at
   * the same time. Derived from a single input, those cannot both be true. See
   * `docs/completion-truth.md`.
   */
  const { verdict: result, retestDue } = sheetVerdict({ loading, progress: saved, sessionResult })

  // startModule's identity changes whenever moduleProgress does, so without a
  // guard this effect re-fires on every progress write and can overwrite the
  // trainee's original startedAt — which is what completeModule measures
  // time-spent from. Quiz.jsx guards it the same way.
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current) return
    startedRef.current = true
    startModule(moduleId)
  }, [moduleId, startModule])

  // Drop the pre-uid key so a shared browser stops handing the next trainee
  // the previous one's spreadsheet link.
  useEffect(() => { clearLegacyKey(LS_PREFIX, moduleId) }, [moduleId])

  // Prefill the link, in order of confidence: what they submitted here, what
  // they submitted on the module that captured the URL, then whatever they had
  // typed but not yet checked before a reload.
  //
  // Latched once the record is in rather than once a URL is found. Latching only
  // on success meant that for a trainee with no stored link the effect stayed
  // live, so a later progress write could overwrite what they were typing.
  //
  // The field is editable throughout the load, so a prefill must never replace
  // something already typed — `setUrl(cur => cur || best)` rather than
  // `setUrl(best)`. Read inside the updater, not from `url`, so the effect does
  // not have to depend on every keystroke.
  useEffect(() => {
    if (hydrated.current || loading) return
    hydrated.current = true
    const best =
      saved?.submission?.url ||
      inherited?.submission?.url ||
      (() => { try { return localStorage.getItem(lsKey) } catch { return null } })()
    if (best) setUrl((cur) => cur || best)
  }, [loading, saved, inherited, lsKey])

  const onUrlChange = (v) => {
    setUrl(v)
    setError(null)
    setSaveFailed(false)
    try { localStorage.setItem(lsKey, v) } catch { /* private mode */ }
  }

  const handleCheck = useCallback(async () => {
    setError(null)

    const spreadsheetId = parseSpreadsheetId(url)
    if (!spreadsheetId) {
      setError("That doesn't look like a Google Sheets link. Open your sheet and copy the URL from the address bar.")
      return
    }

    setStatus('checking')
    try {
      const token = await getSheetsAccessToken()
      let outcome
      try {
        outcome = await runChecks(spreadsheetId, checks, token)
      } catch (err) {
        // The token was live when this started and isn't now. This used to
        // re-request one and retry transparently, but by this point the click
        // that opened the first popup is several network round-trips ago, so
        // the browser blocks the second one and the trainee is told popups are
        // disabled — which isn't the problem. Ask for another press instead:
        // the next one is a fresh gesture and will be allowed.
        if (err?.code === 'auth') {
          clearSheetsToken()
          throw Object.assign(
            new Error('Your access to Google timed out. Press Check my sheet again.'),
            { code: 'auth' }
          )
        }
        throw err
      }

      const checkResult = {
        passed: outcome.passed,
        score: outcome.score,
        checkedAt: new Date().toISOString(),
        results: outcome.results,
      }

      const payload = {
        submission: {
          // The canonical URL rebuilt from the parsed id, never the string the
          // trainee typed: this value is later rendered as a link in the admin
          // console, and an admin is exactly who you would aim a fake link at.
          url: sheetUrlFor(spreadsheetId),
          spreadsheetId,
          submittedAt: new Date().toISOString(),
        },
        checkResult,
      }

      // Record the score the same way a quiz does, so one column in the admin
      // console means "how are they doing" across both kinds of module.
      if (outcome.passed) {
        payload.quizResults = {
          kind: 'sheet-check',
          percentageScore: outcome.score,
          completedAt: new Date().toISOString(),
        }
      }

      // Write FIRST, then tell them. Announcing "module complete" before the
      // write means an offline trainee is told they are done while nothing was
      // recorded — their sheet passes, they move on, and the admin console
      // shows the module as never started.
      const write = await saveModuleProgress(moduleId, payload)
      const done = outcome.passed && write.ok ? await completeModule(moduleId) : { ok: true }

      setSessionResult(checkResult)
      setStatus('done')
      setSaveFailed(!write.ok || !done.ok)
    } catch (err) {
      setStatus('idle')
      setError(err?.message || 'Something went wrong checking your sheet.')
    }
  }, [url, checks, moduleId, saveModuleProgress, completeModule])

  const checking = status === 'checking'
  const passedCount = result?.results?.filter((r) => r.ok).length ?? 0
  const totalCount = result?.results?.length ?? 0

  return (
    <section className="rounded-xl border-2 border-teal-200 bg-teal-50/40 p-6">
      <div className="flex items-start gap-3 mb-4">
        <ShieldCheck className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" />
        <div>
          <h3 className="font-bold text-slate-800">{heading}</h3>
          <p className="text-sm text-slate-600 mt-1">{intro}</p>
        </div>
      </div>

      <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor={`sheet-url-${moduleId}`}>
        Your spreadsheet link
      </label>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          id={`sheet-url-${moduleId}`}
          type="url"
          value={url}
          onChange={(e) => onUrlChange(e.target.value)}
          placeholder="https://docs.google.com/spreadsheets/d/..."
          disabled={checking}
          className="flex-1 rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono
                     focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-teal-400
                     disabled:bg-slate-100"
        />
        <button
          onClick={handleCheck}
          disabled={checking || !url.trim()}
          className="inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 px-5 py-2
                     text-sm font-semibold text-white transition-colors hover:bg-teal-700
                     disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {checking ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Checking…</>
          ) : result ? (
            <><RefreshCw className="w-4 h-4" /> Check again</>
          ) : (
            'Check my sheet'
          )}
        </button>
      </div>

      {!hasSheetsToken() && !result && (
        <p className="mt-2 text-xs text-slate-500">
          Google will ask permission to read your spreadsheets the first time. We read the sheet
          as you — you never have to share it with anyone.
        </p>
      )}

      {sheetUrlFor(parseSpreadsheetId(url)) && (
        <a
          href={sheetUrlFor(parseSpreadsheetId(url))}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-teal-700 hover:underline"
        >
          Open this sheet <ExternalLink className="w-3 h-3" />
        </a>
      )}

      {retestDue && (
        <p className="mt-3 rounded-lg border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
          Your trainer has asked you to do this one again, so the previous result no longer counts.
          Check your sheet still matches and press <strong>Check my sheet</strong>.
        </p>
      )}

      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2"
          >
            <p className="text-sm text-red-700">{error}</p>
          </motion.div>
        )}
      </AnimatePresence>

      {result && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-5"
        >
          <div
            className={`flex items-center gap-2 rounded-lg px-4 py-3 mb-3 ${
              result.passed ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
            }`}
          >
            {result.passed ? <CheckCircle2 className="w-5 h-5" /> : <XCircle className="w-5 h-5" />}
            <span className="font-semibold">
              {result.passed
                ? saveFailed
                  ? 'All checks passed — but we could not save it.'
                  : 'All checks passed — module complete.'
                : `${passedCount} of ${totalCount} checks passed.`}
            </span>
          </div>

          {saveFailed && (
            <p className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Your sheet is right, but recording it failed — you may be offline. Press{' '}
              <strong>Check again</strong> once you're back online, otherwise this module will
              still show as incomplete.
            </p>
          )}

          {!result.passed && (
            <p className="text-sm text-slate-600 mb-3">
              Fix what's flagged below and press <strong>Check again</strong>. You can try as many
              times as you like — nothing is counted against you.
            </p>
          )}

          <ul className="space-y-2">
            {result.results?.map((r, i) => (
              <li
                key={i}
                className={`rounded-lg border px-4 py-3 ${
                  r.ok ? 'border-green-200 bg-white' : 'border-amber-300 bg-white'
                }`}
              >
                <div className="flex items-start gap-2">
                  {r.ok ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  )}
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium ${r.ok ? 'text-slate-700' : 'text-slate-800'}`}>
                      {r.label}
                    </p>
                    {!r.ok && (
                      <div className="mt-1.5 space-y-0.5 text-xs">
                        <p className="text-slate-500">
                          <span className="font-semibold text-slate-600">Expected:</span> {r.expected}
                        </p>
                        <p className="text-slate-500">
                          <span className="font-semibold text-slate-600">Found:</span> {r.actual}
                        </p>
                        {r.hint && <p className="text-amber-700 mt-1">{r.hint}</p>}
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      )}
    </section>
  )
}
